import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import fr from '../../locales/fr/home';
import ar from '../../locales/ar/home';
import es from '../../locales/es/home';

const translations = { fr, ar, es };

const Home = () => {
  const { lang, isRTL } = useLanguage();
  const t = translations[lang];

  return (
    <>
      {/* Hero Section */}
      <div id="accueil" className="relative bg-indigo-600">
        <div className="absolute inset-0">
          <img
            className="w-full h-full object-cover"
            src="/images/hero-bg.jpg"
            alt="Background"
          />
          <div className="absolute inset-0 bg-indigo-600 mix-blend-multiply opacity-50" />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t.hero.title}
          </h1>
          <p className="mt-4 text-2xl font-semibold text-indigo-200">
            {t.hero.subtitle}
          </p>
          <p className="mt-6 text-xl text-indigo-100 max-w-3xl">
            {t.hero.description}
          </p>
          <div className="mt-10">
            <Link
              to={`/${lang}/register`}
              className="inline-block bg-white py-3 px-8 border border-transparent rounded-md text-base font-medium text-indigo-600 hover:bg-indigo-50"
            >
              {t.hero.cta}
            </Link>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="fonctionnalites" className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              {t.features.title}
            </h2>
            <p className="mt-4 text-lg text-gray-500 max-w-3xl mx-auto">
              {t.features.description}
            </p>
          </div>

          <div className="mt-10">
            <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              {/* Feature 1 */}
              <div className="relative">
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <img
                    src="/images/pos.jpg"
                    alt={t.features.items.touch.title}
                    className="h-32 w-32 rounded-lg object-cover"
                  />
                  <div className={isRTL ? 'mr-6' : 'ml-6'}>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t.features.items.touch.title}
                    </h3>
                    <p className="mt-2 text-base text-gray-500">
                      {t.features.items.touch.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="relative">
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <img
                    src="/images/invoice.jpg"
                    alt={t.features.items.tickets.title}
                    className="h-32 w-32 rounded-lg object-cover"
                  />
                  <div className={isRTL ? 'mr-6' : 'ml-6'}>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t.features.items.tickets.title}
                    </h3>
                    <p className="mt-2 text-base text-gray-500">
                      {t.features.items.tickets.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="relative">
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <img
                    src="/images/online-shop.jpg"
                    alt={t.features.items.responsive.title}
                    className="h-32 w-32 rounded-lg object-cover"
                  />
                  <div className={isRTL ? 'mr-6' : 'ml-6'}>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t.features.items.responsive.title}
                    </h3>
                    <p className="mt-2 text-base text-gray-500">
                      {t.features.items.responsive.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Feature 4 */}
              <div className="relative">
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <img
                    src="/images/qr-code.jpg"
                    alt={t.features.items.barcode.title}
                    className="h-32 w-32 rounded-lg object-cover"
                  />
                  <div className={isRTL ? 'mr-6' : 'ml-6'}>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t.features.items.barcode.title}
                    </h3>
                    <p className="mt-2 text-base text-gray-500">
                      {t.features.items.barcode.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Start Section */}
      <div id="quick-start" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              {t.quickStart.title}
            </h2>
            <p className="mt-4 text-lg text-gray-500 max-w-3xl mx-auto">
              {t.quickStart.description}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <div className="text-center">
              <div className="flex justify-center">
                <img
                  src="/images/easy-to-use.jpg"
                  alt={t.quickStart.items.easy}
                  className="h-48 w-48 rounded-lg object-cover"
                />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {t.quickStart.items.easy}
              </h3>
            </div>
            <div className="text-center">
              <div className="flex justify-center">
                <img
                  src="/images/secure.jpg"
                  alt={t.quickStart.items.secure}
                  className="h-48 w-48 rounded-lg object-cover"
                />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {t.quickStart.items.secure}
              </h3>
            </div>
            <div className="text-center">
              <div className="flex justify-center">
                <img
                  src="/images/backup.jpg"
                  alt={t.quickStart.items.backup}
                  className="h-48 w-48 rounded-lg object-cover"
                />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {t.quickStart.items.backup}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Business Types Section */}
      <div id="business-types" className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              {t.businessTypes.title}
            </h2>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home; 